import { Bell } from 'lucide-react';

import AccountMenu from '@/components/nav/account-menu';
import NavBrand from '@/components/nav/nav-brand';
import NavBreadcrumb, { type Crumb } from '@/components/nav/nav-breadcrumb';
import QuickSettingsPopover from '@/components/nav/quick-settings-popover';
import { SearchField } from '@/components/ui/search-field';

interface DashboardHeaderProps {
  /** Optional navigable breadcrumb shown after the brand (e.g. Home / Collection). */
  breadcrumb?: Crumb[];
  /**
   * Optional search box hosted in the top bar.
   *
   * `placeholder` is REQUIRED, not because the box needs ghost text but because it is also the
   * accessible name — this bar has no visible label to point at. Both callers already passed one;
   * typing it optional only meant an unnamed field was reachable by forgetting.
   */
  search?: { value: string; onChange: (v: string) => void; placeholder: string };
}

/** Shared top navbar for the collections browse and collection board pages. */
export default function DashboardHeader({ breadcrumb, search }: DashboardHeaderProps) {
  return (
    <header className='flex h-14 shrink-0 items-center gap-2 border-b border-ws-border bg-ws-surface px-3 sm:gap-3 sm:px-6'>
      <NavBrand className='h-11' />
      {breadcrumb && breadcrumb.length > 0 && <NavBreadcrumb items={breadcrumb} />}

      <div className='ml-auto flex items-center gap-1.5 sm:gap-2'>
        {/* `h-9` so the box keeps the height of the buttons beside it on this bar; everything
            else — the glass, the clear button, the radius, the surface — is the shared field's,
            which is the point. This was a hand-rolled <input> with its own four of those, and
            the ban on raw fields does not reach outside `components/task/`. */}
        {search && (
          <SearchField
            value={search.value}
            onChange={search.onChange}
            placeholder={search.placeholder}
            ariaLabel={search.placeholder}
            className='h-9 w-36 sm:w-60'
          />
        )}
        <QuickSettingsPopover />
        <button className='hidden h-9 w-9 items-center justify-center rounded-lg border border-ws-border text-ws-muted hover:bg-ws-hover sm:flex' aria-label='Notifications'>
          <Bell className='h-4 w-4' />
        </button>
        <AccountMenu />
      </div>
    </header>
  );
}
